import { useState, useMemo, ReactNode } from 'react'
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  Search,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

// Types
export interface ColumnDef<T> {
  id: string
  header: string
  accessorKey?: keyof T | string
  sortable?: boolean
  cell?: (row: T) => ReactNode
  className?: string
  headerClassName?: string
}

export interface Action<T> {
  label: string
  icon?: ReactNode
  onClick: (row: T) => void
  variant?: 'default' | 'destructive'
  show?: (row: T) => boolean
}

export interface FilterConfig {
  activeCount: number
  onOpen: () => void
  label?: string
}

export interface DataTableLabels {
  actions?: string
}

export interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  actions?: Action<T>[]
  isLoading?: boolean
  title?: string
  titleIcon?: ReactNode
  searchPlaceholder?: string
  searchKeys?: (keyof T | string)[]
  searchValue?: string
  onSearchChange?: (value: string) => void
  emptyMessage?: string
  emptyIcon?: ReactNode
  emptyAction?: ReactNode
  onRowClick?: (row: T) => void
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (column: string, order: 'asc' | 'desc') => void
  pagination?: {
    page: number
    limit: number
    total: number
    pages: number
    onPageChange: (page: number) => void
  }
  filterConfig?: FilterConfig
  headerExtra?: ReactNode
  getRowId?: (row: T) => string
  selectable?: boolean
  selectedIds?: Set<string>
  onSelectionChange?: (ids: Set<string>) => void
  labels?: DataTableLabels
}

// Helper to get nested value from object
function getNestedValue<T>(obj: T, path: string): unknown {
  return path.split('.').reduce((acc: unknown, part) => {
    if (acc && typeof acc === 'object' && part in acc) {
      return (acc as Record<string, unknown>)[part]
    }
    return undefined
  }, obj)
}

// Pagination component
function Pagination({
  page,
  pages,
  total,
  limit,
  onPageChange,
}: {
  page: number
  pages: number
  total: number
  limit: number
  onPageChange: (page: number) => void
}) {
  const from = (page - 1) * limit + 1
  const to = Math.min(page * limit, total)

  // Build page numbers: always show first, last, current, and neighbors
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    if (pages <= 7) {
      return Array.from({ length: pages }, (_, i) => i + 1)
    }

    const items: (number | 'ellipsis')[] = [1]

    if (page > 3) items.push('ellipsis')

    const start = Math.max(2, page - 1)
    const end = Math.min(pages - 1, page + 1)
    for (let i = start; i <= end; i++) items.push(i)

    if (page < pages - 2) items.push('ellipsis')

    items.push(pages)
    return items
  }

  return (
    <div className="flex items-center justify-between pt-4 border-t mt-4">
      <p className="text-sm text-muted-foreground">
        {from}–{to} of {total.toLocaleString()}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(1)}
          disabled={page === 1}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {getPageNumbers().map((item, idx) =>
          item === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-muted-foreground text-sm">
              ...
            </span>
          ) : (
            <Button
              key={item}
              variant={item === page ? 'default' : 'outline'}
              size="icon"
              className={`h-8 w-8 text-xs ${
                item === page ? 'bg-[#2D8A5E] hover:bg-[#2d5c3f] text-white' : ''
              }`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(page + 1)}
          disabled={page === pages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(pages)}
          disabled={page === pages}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

export function DataTable<T extends object>({
  data,
  columns,
  actions,
  isLoading = false,
  title,
  titleIcon,
  searchPlaceholder,
  searchKeys = [],
  searchValue,
  onSearchChange,
  emptyMessage,
  emptyIcon,
  emptyAction,
  onRowClick,
  sortBy,
  sortOrder = 'desc',
  onSort,
  pagination,
  filterConfig,
  getRowId,
  selectable = false,
  selectedIds,
  onSelectionChange,
  headerExtra,
  labels,
}: DataTableProps<T>) {
  const [localSearch, setLocalSearch] = useState('')

  // Use controlled or uncontrolled search
  const currentSearch = searchValue !== undefined ? searchValue : localSearch
  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value)
    } else {
      setLocalSearch(value)
    }
  }

  // Local search filtering (client-side) - only when not using controlled search
  const filteredData = useMemo(() => {
    if (onSearchChange) return data // Server-side search
    if (!currentSearch || searchKeys.length === 0) return data

    const searchLower = currentSearch.toLowerCase()
    return data.filter((row) =>
      searchKeys.some((key) => {
        const value = getNestedValue(row, key as string)
        if (value == null) return false
        return String(value).toLowerCase().includes(searchLower)
      })
    )
  }, [data, currentSearch, searchKeys, onSearchChange])

  // Selection helpers
  const isSelectable = selectable && getRowId && onSelectionChange
  const visibleRowIds = useMemo(() => {
    if (!isSelectable) return []
    return filteredData.map((row) => getRowId(row))
  }, [filteredData, isSelectable, getRowId])

  const allVisibleSelected = isSelectable && visibleRowIds.length > 0 && visibleRowIds.every((id) => selectedIds?.has(id))
  const someVisibleSelected = isSelectable && visibleRowIds.some((id) => selectedIds?.has(id))

  const handleSelectAll = () => {
    if (!onSelectionChange || !getRowId) return
    if (allVisibleSelected) {
      // Deselect all visible
      const next = new Set(selectedIds)
      for (const id of visibleRowIds) next.delete(id)
      onSelectionChange(next)
    } else {
      // Select all visible
      const next = new Set(selectedIds)
      for (const id of visibleRowIds) next.add(id)
      onSelectionChange(next)
    }
  }

  const handleSelectRow = (rowId: string) => {
    if (!onSelectionChange) return
    const next = new Set(selectedIds)
    if (next.has(rowId)) {
      next.delete(rowId)
    } else {
      next.add(rowId)
    }
    onSelectionChange(next)
  }

  const handleSort = (column: string) => {
    if (!onSort) return
    const newOrder = sortBy === column && sortOrder === 'desc' ? 'asc' : 'desc'
    onSort(column, newOrder)
  }

  const SortableHeader = ({
    column,
    children,
  }: {
    column: ColumnDef<T>
    children: ReactNode
  }) => {
    if (!column.sortable || !onSort) {
      return (
        <TableHead className={column.headerClassName}>
          {children}
        </TableHead>
      )
    }

    const isActive = sortBy === column.id
    const isAsc = sortOrder === 'asc'
    const SortIcon = isActive ? (isAsc ? ArrowUp : ArrowDown) : ArrowUpDown

    return (
      <TableHead
        className={`cursor-pointer select-none hover:bg-muted/30 transition-all duration-150 group ${column.headerClassName || ''}`}
        onClick={() => handleSort(column.id)}
      >
        <div className="flex items-center gap-2">
          {children}
          <SortIcon
            className={`h-3.5 w-3.5 transition-all duration-150 ${
              isActive
                ? 'text-[#3d7a4f] opacity-100'
                : 'text-muted-foreground/40 opacity-0 group-hover:opacity-100'
            }`}
          />
        </div>
      </TableHead>
    )
  }

  const renderCellValue = (row: T, column: ColumnDef<T>) => {
    if (column.cell) {
      return column.cell(row)
    }
    if (column.accessorKey) {
      const value = getNestedValue(row, column.accessorKey as string)
      if (value == null) return '-'
      return String(value)
    }
    return '-'
  }

  const visibleActions = (row: T) => {
    if (!actions) return []
    return actions.filter((action) => !action.show || action.show(row))
  }

  const showSearch = searchKeys.length > 0 || onSearchChange
  const showFilterButton = !!filterConfig

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {title && (
            <CardTitle className="flex items-center gap-2">
              {titleIcon}
              {title}
            </CardTitle>
          )}
          {(showSearch || showFilterButton || headerExtra) && (
            <div className="flex items-center gap-2 flex-wrap">
              {headerExtra}
              {showSearch && (
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder || 'Search...'}
                    value={currentSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-9"
                  />
                </div>
              )}
              {showFilterButton && (
                <Button
                  variant="outline"
                  size="default"
                  onClick={filterConfig.onOpen}
                  className="relative"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  {filterConfig.label || "Filters"}
                  {filterConfig.activeCount > 0 && (
                    <Badge
                      variant="default"
                      className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-[#1E9E6B]"
                    >
                      {filterConfig.activeCount}
                    </Badge>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center gap-4 text-muted-foreground">
            {emptyIcon}
            <p>{emptyMessage || 'No results found'}</p>
            {emptyAction}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSelectable && (
                    <TableHead className="w-[40px]" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                  )}
                  {columns.map((column) => (
                    <SortableHeader key={column.id} column={column}>
                      {column.header}
                    </SortableHeader>
                  ))}
                  {actions && actions.length > 0 && (
                    <TableHead className="text-right w-[100px]">
                      {labels?.actions || "Actions"}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((row, index) => {
                  const rowId = getRowId ? getRowId(row) : index.toString()
                  const rowActions = visibleActions(row)

                  return (
                    <TableRow
                      key={rowId}
                      className={`${onRowClick ? 'cursor-pointer' : ''} ${isSelectable && selectedIds?.has(rowId) ? 'bg-muted/40' : ''}`}
                      onClick={() => onRowClick?.(row)}
                    >
                      {isSelectable && (
                        <TableCell
                          className="w-[40px]"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSelectRow(rowId)
                          }}
                        >
                          <Checkbox
                            checked={selectedIds?.has(rowId) ?? false}
                            onCheckedChange={() => handleSelectRow(rowId)}
                          />
                        </TableCell>
                      )}
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          className={column.className}
                        >
                          {renderCellValue(row, column)}
                        </TableCell>
                      ))}
                      {actions && actions.length > 0 && (
                        <TableCell
                          className="text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {rowActions.length > 0 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {rowActions.map((action, actionIndex) => (
                                  <DropdownMenuItem
                                    key={actionIndex}
                                    onClick={() => action.onClick(row)}
                                    className={
                                      action.variant === 'destructive'
                                        ? 'text-destructive'
                                        : ''
                                    }
                                  >
                                    {action.icon}
                                    {action.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            </div>

            {pagination && pagination.pages > 1 && (
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={pagination.onPageChange}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default DataTable
